import { Injectable, OnDestroy, DOCUMENT, inject } from "@angular/core";
import {
  HjFn,
  IHotjarSettings,
  NGX_HOTJAR_SETTINGS_TOKEN,
  NgxHotjarService,
  WINDOW,
  hotjarInitializer,
} from "ngx-hotjar";
import { ConsentService } from "./consent-service";
import { Subscription } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class HotjarService extends NgxHotjarService implements OnDestroy {
  private realHjFn?: HjFn;
  private initializer: () => Promise<void>;
  private initRun = false;
  private queue?: any[] = [];
  private enabled = false;
  private subscription = new Subscription();

  constructor() {
    const hotjarSettings = inject<IHotjarSettings>(NGX_HOTJAR_SETTINGS_TOKEN);
    const win = inject<Window>(WINDOW);
    const doc = inject<Document>(DOCUMENT);
    const consentService = inject(ConsentService);
    const wrappedHjFn: HjFn = Object.defineProperties(
      ((...args: Parameters<HjFn>): ReturnType<HjFn> => {
        if (!this.enabled) return;
        if (this.realHjFn) return this.realHjFn(...args);
        if (this.queue) this.queue.push(args);
      }) as HjFn,
      {
        q: {
          get: () => {
            return this.realHjFn?.q as HjFn["q"];
          },
        },
      } satisfies {
        [key in Exclude<keyof HjFn, keyof Function>]: TypedPropertyDescriptor<
          HjFn[key]
        >;
      },
    );
    super(wrappedHjFn, hotjarSettings);
    this.initializer = hotjarInitializer(hotjarSettings, doc, win);
    this.subscription.add(
      consentService.hotjarEnabled.subscribe((enabled) =>
        enabled ? this.enable() : this.disable(),
      ),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public enable() {
    this.enabled = true;
    if (!this.initRun) {
      this.initRun = true;
      this.initHotjar();
    }
  }

  public disable() {
    this.enabled = false;
    if (this.queue) this.queue.length = 0;
  }

  private async initHotjar() {
    try {
      await this.initializer();
      const { hj } = window as { hj?: HjFn };
      this.realHjFn = hj;
      if (this.queue && hj) {
        this.queue.forEach((args) => {
          hj(...args);
        });
      }
    } finally {
      delete this.queue;
    }
  }
}
